import Text from '@/components/ui/custom/dataDisplay/Text';
import InfinityList from '@/components/ui/custom/InfinityList';
import Skeleton from '@/components/ui/custom/Skeleton';
import LegalInformationCourseCollapse from '@/components/_deprecated/courses/LegalInformationCourseCollapse';
import { KlassProgressProps } from '@/components/_deprecated/courses/student/KlassProgress';
import LessonBar from '@/components/_deprecated/lessons/LessonBar/LessonBar';
import { REQUEST_STALE_TIME_IN_MS } from '@/constants';
import useAuth from '@/data/hook/useAuth';
import useInfiniteService from '@/data/hook/useInfiniteService';
import useStudentContext from '@/data/hook/useStudentContext';
import {
  lessonsProgressesQueryKeys,
  resourceProgressQuerykeys,
  resourceQuerykeys,
  scheduledLessonsQueryKeys,
} from '@/data/services/querykeys';
import LessonProgress from '@/models/LessonProgress';
import ScheduledLesson from '@/models/ScheduledLesson';
import { SimplifiedCourseProgress } from '@/models/StudentCourseProgress';
import LoadingView from '@/pages/courses/LoadingView';
import { enrollmentValidation } from '@/utils/enrollmentValidation';
import { getErrorMessage } from '@/utils/getErrorMessage';
import { isBlockedLesson } from '@/utils/isBlockedLesson';
import { useEffect } from 'react';

type StudentKlassLessonsProps = Omit<KlassProgressProps, 'enabled'> & {
  lessonBlockId?: number;
};

export default function StudentKlassLessons({
  progress,
  lessonBlockId,
}: StudentKlassLessonsProps) {
  const validEnrollment = enrollmentValidation(progress.enrollmentStatus);
  const shouldUseKlassId = validEnrollment && !!progress.klassId;
  const { user } = useAuth();
  const {
    results: lessonProgresses,
    isInitialLoading: isLoadingLessonProgresses,
    error: lessonProgressesError,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useInfiniteService({
    ...lessonsProgressesQueryKeys.list({
      klassId: shouldUseKlassId ? progress.klassId : undefined,
      courseProgressId: [progress.id],
      lessonBlockId: lessonBlockId ? [lessonBlockId] : undefined,
      coursePathId: shouldUseKlassId ? undefined : [progress.coursePath.id],
      orderBy: shouldUseKlassId ? 'scheduled_lesson_order' : 'lesson_order',
      scheduledLessonActive: shouldUseKlassId || undefined,
      studentId: user ? [user?.id] : undefined,
    })._ctx.infinity,
    staleTime: REQUEST_STALE_TIME_IN_MS,
  });
  const {
    results: scheduledLessons,
    isInitialLoading: isLoadingScheduledLessons,
    hasNextPage: hasNextScheduledLessonsPage,
    isFetchingNextPage: isFetchingNextScheduledLessonsPage,
    fetchNextPage: fetchNextScheduledLessonsPage,
  } = useInfiniteService({
    ...scheduledLessonsQueryKeys.list({
      pageSize: 25,
      klassId: shouldUseKlassId ? progress.klassId : undefined,
    })._ctx.infinity,
    enabled: shouldUseKlassId,
    staleTime: REQUEST_STALE_TIME_IN_MS,
  });

  useEffect(() => {
    if (lessonProgresses.length <= scheduledLessons.length) return;

    const isLoadingPage =
      isFetchingNextScheduledLessonsPage || isLoadingScheduledLessons;

    if (!hasNextScheduledLessonsPage || isLoadingPage) return;

    fetchNextScheduledLessonsPage();
  }, [
    lessonProgresses,
    fetchNextScheduledLessonsPage,
    hasNextScheduledLessonsPage,
    isFetchingNextScheduledLessonsPage,
    isLoadingScheduledLessons,
    scheduledLessons,
  ]);

  if (isLoadingLessonProgresses) return <LoadingView />;

  if (lessonProgressesError)
    return (
      <Text
        testId="error"
        text={getErrorMessage(lessonProgressesError)}
        format="rubik-500"
        className="text-primary"
      />
    );

  return (
    <InfinityList
      hasNextPage={hasNextPage}
      isFetchingNextPage={isFetchingNextPage}
      onReachEnd={fetchNextPage}
      className="gap-4"
    >
      {lessonProgresses.map(lessonProgress => {
        const scheduledLesson = scheduledLessons.find(
          scheduledLesson =>
            scheduledLesson.lesson.id === lessonProgress.lesson.id,
        );
        const isFetchingNextPage =
          isFetchingNextScheduledLessonsPage && !scheduledLesson;
        const scheduledLessonLoading =
          isLoadingScheduledLessons || isFetchingNextPage;

        return (
          <LessonItem
            key={lessonProgress.id}
            lessonProgress={lessonProgress}
            progress={progress}
            isLoadingScheduledLessons={scheduledLessonLoading}
            scheduledLesson={scheduledLesson}
          />
        );
      })}

      <LegalInformationCourseCollapse course={progress.coursePath} />
    </InfinityList>
  );
}

type LessonItemProps = {
  lessonProgress: LessonProgress;
  progress: SimplifiedCourseProgress;
  scheduledLesson?: ScheduledLesson;
  isLoadingScheduledLessons?: boolean;
};

function LessonItem({
  lessonProgress,
  progress,
  scheduledLesson,
  isLoadingScheduledLessons,
}: LessonItemProps) {
  const { nextLesson } = useStudentContext();

  const scheduledLessonResourcesIds = scheduledLesson?.resources || [];

  const lessonResourcesIds =
    lessonProgress.lesson?.resources || scheduledLesson?.lesson.resources || [];

  const allResourcesIds = lessonResourcesIds.concat(
    scheduledLessonResourcesIds,
  );

  const { results: resourceProgress } = useInfiniteService({
    ...resourceProgressQuerykeys.list({ resourceId: allResourcesIds })._ctx
      .infinity,
    enabled: !!allResourcesIds.length,
  });

  const { results: resources } = useInfiniteService({
    ...resourceQuerykeys.list({ id: allResourcesIds })._ctx.infinity,
    enabled: !!allResourcesIds.length,
  });

  const blocked = isBlockedLesson({
    datetime: scheduledLesson?.datetime,
    lessonId: lessonProgress.lesson.id,
    nextLesson,
    hasDone: scheduledLesson?.hasDone,
    presence: lessonProgress.scheduledLessonReport?.presence,
  });

  if (isLoadingScheduledLessons)
    return (
      <Skeleton className="flex h-[75px] w-full rounded-lg bg-secondary-content" />
    );

  return (
    <LessonBar
      slugCourseName={progress.coursePath.slug}
      lesson={lessonProgress.lesson}
      color="secondary"
      lessonProgress={lessonProgress}
      klassId={!!scheduledLesson ? progress.klassId : undefined}
      scheduledLesson={scheduledLesson}
      blocked={blocked}
      resourceProgress={resourceProgress}
      resources={resources}
    />
  );
}
